import { useEffect, useState } from "react";

/**
 * Samples the average luminance of an image (drawn tiny into an offscreen
 * canvas) to decide whether it reads as dark. Used by every page that floats a
 * transparent header over hero art (playlist, podcast) to flip the back label
 * and action icons to a light tone when the art underneath is dark.
 *
 * Returns null while loading, and whenever the image can't be inspected — e.g. a
 * cross-origin image without CORS headers taints the canvas and `getImageData`
 * throws. Callers treat null as "not dark" and keep the default dark tone.
 */
export function useHeroImageIsDark(url: string | null): boolean | null {
  const [isDark, setIsDark] = useState<boolean | null>(null);

  useEffect(() => {
    setIsDark(null);
    if (url === null) return;

    let cancelled = false;
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => {
      if (cancelled) return;
      const canvas = document.createElement("canvas");
      const sampleSize = 16;
      canvas.width = sampleSize;
      canvas.height = sampleSize;
      const context = canvas.getContext("2d");
      if (context === null) return;
      context.drawImage(image, 0, 0, sampleSize, sampleSize);

      try {
        const { data } = context.getImageData(0, 0, sampleSize, sampleSize);
        let luminanceSum = 0;
        let pixelCount = 0;
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          if (r === undefined || g === undefined || b === undefined) continue;
          // Rec. 709 relative luminance.
          luminanceSum += 0.2126 * r + 0.7152 * g + 0.0722 * b;
          pixelCount += 1;
        }
        if (pixelCount === 0) return;
        setIsDark(luminanceSum / pixelCount < 140);
      } catch {
        // Tainted canvas (cross-origin image without CORS) — leave as null.
      }
    };
    image.src = url;

    return () => {
      cancelled = true;
    };
  }, [url]);

  return isDark;
}
